<template>
  <div class="sales-order-lines">
    <ProductConfiguratorForm
      v-if="configuring"
      :key="configuring.key"
      :product="configuring.product"
      :initial-configuration="configuring.existing?.configuration || null"
      :submit-label="
        configuring.existing
          ? $t('sales_orders.save_configuration')
          : $t('sales_orders.add_to_order')
      "
      :cancel-label="$t('common.cancel')"
      @submit="applyConfiguration"
      @cancel="stopConfiguring"
    />

    <template v-else>
      <section class="sales-order-lines__section">
        <SearchableProductSelect
          id="sales-order-add-product"
          :key="productPickerKey"
          :placeholder="$t('sales_orders.add_product')"
          @productSelected="handleProductSelected"
        />
        <p v-if="loadingProduct" class="sales-order-lines__hint">{{ $t('common.loading') }}</p>

        <ul v-if="productLines.length" class="sales-order-lines__list">
          <li v-for="line in productLines" :key="line.key" class="sales-order-lines__row">
            <div class="sales-order-lines__row-main">
              <strong class="sales-order-lines__row-title">{{ line.title }}</strong>
              <span v-if="line.sku" class="sales-order-lines__row-sku">{{ line.sku }}</span>
              <span
                v-if="line.hidden"
                class="sales-order-lines__hidden-badge"
                :title="$t('sales_orders.hidden_hint')"
              >
                <BaseIcon name="elements_hidden" :size="11" />
                {{ $t('sales_orders.hidden_badge') }}
              </span>
              <p v-if="line.details" class="sales-order-lines__row-details">{{ line.details }}</p>
              <button
                v-if="line.configurable"
                type="button"
                class="sales-order-lines__row-link"
                @click="reconfigure(line)"
              >
                {{ $t('sales_orders.reconfigure') }}
              </button>
            </div>
            <div class="sales-order-lines__qty">
              <NumberInput
                :id="`sales-order-product-qty-${line.key}`"
                :label="$t('sales_orders.line_quantity')"
                :model-value="line.quantity"
                :min="1"
                @update:model-value="(value) => updateLine(line.key, { quantity: value })"
              />
            </div>
            <div class="sales-order-lines__amount">
              <template v-if="pricedLineTotal(line) != null">
                {{ formatCurrency(pricedLineTotal(line)) }}
              </template>
              <span v-else class="sales-order-lines__pending">…</span>
            </div>
            <div class="sales-order-lines__actions">
              <Btn
                variant="plain"
                type="button"
                icon="elements_hidden"
                :class="{ 'sales-order-lines__toggle--active': line.hidden }"
                :title="
                  line.hidden
                    ? $t('sales_orders.show_to_customer')
                    : $t('sales_orders.hide_from_customer')
                "
                :aria-pressed="Boolean(line.hidden)"
                @click="toggleHidden(line.key)"
              />
              <Btn
                variant="plain"
                type="button"
                icon="trash"
                :title="$t('common.delete')"
                @click="removeLine(line.key)"
              />
            </div>
          </li>
        </ul>

        <p v-else class="sales-order-lines__empty">{{ $t('sales_orders.no_products_yet') }}</p>
      </section>

      <section class="sales-order-lines__section">
        <h4 class="sales-order-lines__heading">{{ $t('sales_orders.other_lines') }}</h4>
        <p class="sales-order-lines__hint">{{ $t('sales_orders.other_lines_hint') }}</p>

        <div v-for="line in manualLines" :key="line.key" class="sales-order-lines__manual">
          <TextInput
            :id="`sales-order-line-${line.key}`"
            :label="$t('sales_orders.line_description')"
            :model-value="line.description"
            @update:model-value="(value) => updateLine(line.key, { description: value })"
          />
          <NumberInput
            :id="`sales-order-qty-${line.key}`"
            :label="$t('sales_orders.line_quantity')"
            :model-value="line.quantity"
            :min="1"
            @update:model-value="(value) => updateLine(line.key, { quantity: value })"
          />
          <CurrencyInput
            :id="`sales-order-price-${line.key}`"
            :label="$t('sales_orders.line_price')"
            :model-value="line.unit_price"
            @update:model-value="(value) => updateLine(line.key, { unit_price: value })"
          />
          <div class="sales-order-lines__actions">
            <Btn
              variant="plain"
              type="button"
              icon="elements_hidden"
              :class="{ 'sales-order-lines__toggle--active': line.hidden }"
              :title="
                line.hidden
                  ? $t('sales_orders.show_to_customer')
                  : $t('sales_orders.hide_from_customer')
              "
              :aria-pressed="Boolean(line.hidden)"
              @click="toggleHidden(line.key)"
            />
            <Btn
              variant="plain"
              type="button"
              icon="trash"
              :title="$t('common.delete')"
              @click="removeLine(line.key)"
            />
          </div>
        </div>

        <div>
          <Btn variant="dashed" type="button" icon="plus" @click="addManualLine">
            {{ $t('sales_orders.add_line') }}
          </Btn>
        </div>
      </section>

      <p v-if="pricingError" class="sales-order-lines__error">{{ pricingError }}</p>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
  import { useToastStore } from '@/store/toastStore';
  import { pricingService } from '@/services/pricingService';
  import { hasSalesOrderLines } from '@/utils/salesOrderLines';
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import CurrencyInput from '@/components/inputs/CurrencyInput.vue';
  import SearchableProductSelect from '@/components/inputs/SearchableProductSelect.vue';
  import ProductConfiguratorForm from '@/components/products/ProductConfiguratorForm.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';
  import productService from '@shared/services/productService';

  const CONTENT_ONLY_TYPES = ['notice', 'image', 'photo'];

  let keySeed = 0;
  const nextKey = () => `line-${Date.now()}-${keySeed++}`;

  /**
   * Lines of a sales order being built: configured catalog products plus free
   * text lines. Products are configured inline; prices come from the API
   * (margin + VAT basis live there).
   */
  export default {
    name: 'SalesOrderLinesEditor',
    components: {
      TextInput,
      NumberInput,
      CurrencyInput,
      SearchableProductSelect,
      ProductConfiguratorForm,
      Btn,
      BaseIcon,
    },
    props: {
      modelValue: { type: Array, default: () => [] },
    },
    emits: ['update:modelValue', 'priced', 'configuring'],
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();

      const loadingProduct = ref(false);
      const productPickerKey = ref(0);
      const configuring = ref(null);

      watch(
        () => Boolean(configuring.value),
        (value) => emit('configuring', value)
      );

      const productLines = computed(() => props.modelValue.filter((l) => l.kind === 'product'));
      const manualLines = computed(() => props.modelValue.filter((l) => l.kind === 'manual'));

      const priced = ref(null);
      const pricing = ref(false);
      const pricingError = ref('');
      let priceTimer = null;
      let priceRequest = 0;

      const publishPricing = () =>
        emit('priced', { result: priced.value, loading: pricing.value, error: pricingError.value });

      const refreshPricing = async () => {
        const request = ++priceRequest;
        if (!hasSalesOrderLines(props.modelValue)) {
          priced.value = null;
          pricing.value = false;
          pricingError.value = '';
          publishPricing();
          return;
        }
        pricing.value = true;
        pricingError.value = '';
        publishPricing();
        try {
          const result = await pricingService.quote({ lines: props.modelValue });
          if (request === priceRequest) priced.value = result;
        } catch (error) {
          if (request === priceRequest) {
            priced.value = null;
            pricingError.value = error?.response?.data?.error || $t('sales_orders.pricing_failed');
          }
        } finally {
          if (request === priceRequest) {
            pricing.value = false;
            publishPricing();
          }
        }
      };

      watch(
        () => props.modelValue,
        () => {
          clearTimeout(priceTimer);
          priceTimer = setTimeout(refreshPricing, 400);
        },
        { deep: true, immediate: true }
      );
      onBeforeUnmount(() => clearTimeout(priceTimer));

      // Priced lines include option/upsell child lines; everything with the
      // same product_index belongs to the same editor line.
      const pricedLineTotal = (line) => {
        if (pricing.value || !priced.value) return null;
        const ordinal = productLines.value.findIndex((l) => l.key === line.key);
        const group = priced.value.items.filter(
          (item) => item.source === 'configurator' && item.product_index === ordinal
        );
        if (!group.length) return null;
        return group.reduce(
          (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0),
          0
        );
      };

      const emitLines = (lines) => emit('update:modelValue', lines);

      const updateLine = (key, patch) => {
        emitLines(
          props.modelValue.map((line) => (line.key === key ? { ...line, ...patch } : line))
        );
      };

      const removeLine = (key) => {
        emitLines(props.modelValue.filter((line) => line.key !== key));
      };

      // Hidden lines still count in the total; customer documents leave them out.
      const toggleHidden = (key) => {
        const line = props.modelValue.find((l) => l.key === key);
        if (line) updateLine(key, { hidden: !line.hidden });
      };

      const addManualLine = () => {
        emitLines([
          ...props.modelValue,
          { kind: 'manual', key: nextKey(), description: '', quantity: 1, unit_price: 0 },
        ]);
      };

      const resolveProduct = (data) => (data?.product ? data.product : data);

      const isConfigurable = (product) =>
        Array.isArray(product?.options) &&
        product.options.some((option) => !CONTENT_ONLY_TYPES.includes(option.type));

      const buildProductLine = (product, result, existing = null) => ({
        kind: 'product',
        key: existing?.key || nextKey(),
        product_id: product.id,
        title: product.title || product.name || '',
        sku: product.sku || product.base_sku || '',
        configurable: isConfigurable(product),
        configuration: result?.configuration || {},
        pricing: result?.pricing || null,
        details: result?.detailsSummary || '',
        quantity: existing?.quantity || 1,
        hidden: existing ? Boolean(existing.hidden) : product.feedPurpose === 'materials',
      });

      const startConfiguring = (product, existing = null) => {
        configuring.value = { key: nextKey(), product, existing };
      };

      const stopConfiguring = () => {
        configuring.value = null;
      };

      const applyConfiguration = (result) => {
        const { product, existing } = configuring.value;
        const line = buildProductLine(product, result, existing);
        if (existing) {
          emitLines(props.modelValue.map((l) => (l.key === existing.key ? line : l)));
        } else {
          emitLines([...props.modelValue, line]);
        }
        stopConfiguring();
      };

      const loadFullProduct = async (productId) => {
        loadingProduct.value = true;
        try {
          return resolveProduct(await productService.getProductById(productId));
        } finally {
          loadingProduct.value = false;
        }
      };

      const notifyProductMissing = (error) => {
        console.error('Failed to load product for sales order:', error);
        toastStore.addToast({
          type: 'error',
          message: $t('proposals.configurator.product_not_found'),
        });
      };

      const handleProductSelected = async (product) => {
        productPickerKey.value += 1;
        try {
          const full = (await loadFullProduct(product.id)) || product;
          if (isConfigurable(full)) {
            startConfiguring(full);
          } else {
            emitLines([...props.modelValue, buildProductLine(full, null)]);
          }
        } catch (error) {
          notifyProductMissing(error);
        }
      };

      const reconfigure = async (line) => {
        try {
          const full = await loadFullProduct(line.product_id);
          if (!full) throw new Error('Product not found');
          startConfiguring(full, line);
        } catch (error) {
          notifyProductMissing(error);
        }
      };

      const formatCurrency = (amount) =>
        new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
          Number(amount) || 0
        );

      return {
        loadingProduct,
        productPickerKey,
        configuring,
        productLines,
        manualLines,
        pricingError,
        pricedLineTotal,
        updateLine,
        removeLine,
        toggleHidden,
        addManualLine,
        handleProductSelected,
        reconfigure,
        applyConfiguration,
        stopConfiguring,
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  .sales-order-lines {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    min-height: 0;
    height: 100%;

    &__section {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__heading {
      margin: 0;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--slate-500);
    }

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
      border-top: 1px solid var(--slate-200);
    }

    &__row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 5.5rem 7.5rem auto;
      gap: 1rem;
      align-items: start;
      padding: 1rem 0;
      border-bottom: 1px solid var(--slate-200);

      @include sm-down {
        grid-template-columns: minmax(0, 1fr) auto;
      }
    }

    &__row-main {
      min-width: 0;
    }

    &__row-title {
      display: block;
      font-size: 0.9375rem;
      color: var(--slate-900);
    }

    &__row-sku {
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__hidden-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      margin-left: 0.5rem;
      padding: 0 0.375rem;
      border-radius: 999px;
      background: var(--amber-50);
      color: var(--amber-700);
      font-size: 0.6875rem;
      font-weight: 500;
      vertical-align: middle;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    &__toggle--active {
      color: var(--amber-700);
    }

    &__row-details {
      margin: 0.375rem 0 0;
      font-size: 0.8125rem;
      line-height: 1.5;
      color: var(--slate-600);
      white-space: pre-line;
      @include ellipsis(4);
    }

    &__row-link {
      margin-top: 0.375rem;
      padding: 0;
      border: 0;
      background: none;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--brand-600);
      cursor: pointer;

      @include hover {
        text-decoration: underline;
      }
    }

    &__qty :deep(.form-group) {
      margin-bottom: 0;
    }

    &__amount {
      padding-top: 1.75rem;
      text-align: right;
      font-size: 0.9375rem;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      color: var(--slate-900);
    }

    &__pending {
      color: var(--slate-400);
      font-weight: 400;
    }

    &__empty {
      margin: 0;
      padding: 1.5rem 0;
      text-align: center;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__manual {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 5.5rem 7.5rem auto;
      gap: 1rem;
      align-items: end;

      @include sm-down {
        grid-template-columns: 1fr 1fr;
      }
    }

    &__hint {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__error {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--red-600);
    }
  }
</style>
